import angular from 'angular';

import {
    Departments,
    Items,
    Printers,
    Sales,
    SalesItems,
    SalesItemsPriceChanges,
    SalesPriceChanges,
} from 'tilby-models';

import { XMLNode } from 'app/libs/XmlNode';

import {
    padCenter,
    stringToLines
} from 'src/app/shared/string-utils';

import { groupBy, keyBy } from 'src/app/shared/utils';

import {
    EpsonUtils,
    EpsonHttpAdditionalInfo,
    EpsonHttpResult
} from 'src/app/shared/epson-utils';

import {
    FiscalUtilsService
} from 'app/modules/core/service/fiscal-utils/fiscal-utils';

import {
    FiscalPayment
} from 'src/app/shared/model/it-fiscal-printer.model';

import {
    SimplifiedDocument
} from 'src/app/features/printers/drivers';

import {
    EpsonAlign
} from 'src/app/shared/model/nf-thermal-printer.model';

export class EpsonUtilsService {
    static $inject = [
        "fiscalUtils"
    ];

    constructor(
        private fiscalUtils: FiscalUtilsService
    ) {
    }

    public getPrinterEndpoint(ipAddress: string, useSSL: boolean) {
        return EpsonUtils.getPrinterEndpoint(ipAddress, useSSL);
    }

    public addSoapEnvelope(command: string) {
        return EpsonUtils.addSoapEnvelope(command);
    }

    public getDirectIOCommand(command: string, data: string) {
        return EpsonUtils.getDirectIOCommand(command, data);
    }


    public parseCommercialDocumentDate(documentContent: string) {
        return EpsonUtils.parseCommercialDocumentDate(documentContent);
    }

    public cleanupSaleDocument(documentContent?: string) {
        return EpsonUtils.cleanupSaleDocument(documentContent);
    }

    public parseEpsonXml(responseData: any) {
        return EpsonUtils.parseEpsonXml(responseData);
    }

    public async eposSend(address: string, request: string, options?: { timeout?: number }) {
        const soap = this.addSoapEnvelope(request);
        let responseData;

        const reqHeaders = {
            'Content-Type': 'text/xml; charset=UTF-8',
            'If-Modified-Since': 'Thu, 01 Jan 1970 00:00:00 GMT',
            'SOAPAction': '""'
        };

        try {
            responseData = await this.fiscalUtils.sendRequest(address, { data: soap, timeout: options?.timeout || 60000, headers: reqHeaders, method: 'POST' });
        } catch (responseError: any) {
            throw {
                status: responseError.status,
                responseText: responseError.data
            };
        }

        const res = this.parseEpsonXml(responseData);

        if (!res.result) {
            throw res;
        }

        return res;
    }

    public async sendCommands(printer: Printers, commands: string): Promise<[EpsonHttpResult, EpsonHttpAdditionalInfo]> {
        const printerEndpoint = this.getPrinterEndpoint(printer.ip_address || '', !!(printer.ssl));
        let res;

        try {
            res = await this.eposSend(printerEndpoint, commands);
        } catch (err: any) {
            throw 'CONNECTION_ERROR';
        }

        switch (res.result.status) {
            case 2: //OK
                return [res.result, res.addInfo];
            case 0:
                if (['FP_NO_ANSWER', 'LAN_ERROR', 'LAN_TIME_OUT'].includes(res.result.code!)) {
                    //printContentByDate could return an FP_NO_ANSWER, so in this case we pretend to have a positive answer
                    if (res.result.code == "FP_NO_ANSWER" && commands.includes('printContentByDate')) {
                        return [res.result, res.addInfo];
                    }

                    throw 'CONNECTION_ERROR';
                }

                throw `EPSON.${res.result.code}`;
            default:
                throw `EPSON.ERROR_${res.result.status}`;
        }
    }

    public getPriceChangeCommand(priceChange: SalesPriceChanges | SalesItemsPriceChanges, amount: number, departmentId?: number, options?: { xml7Mode?: boolean }) {
        if (amount == null) {
            return;
        }

        let adjustmentType;

        /*
            adjustmentType determines discount/uplift operation to perform:
            0 = Discount on last sale
            3 = Discount on a department
            5 = Uplift on lastsale
            8 = Uplift on a department
            10 = ACCONTO
            11 = OMAGGIO
            12 = BUONO MONO USO
        */

        switch (priceChange.type) {
            case 'discount_fix':
            case 'discount_perc':
                adjustmentType = departmentId == null ? 2 : 3;
                break;
            case 'surcharge_fix':
            case 'surcharge_perc':
                adjustmentType = departmentId == null ? 7 : 8;
                break;
            case 'gift':
                if (departmentId != null) {
                    adjustmentType = options?.xml7Mode ? 11 : 3;
                }
                break;
        }

        if (adjustmentType == null) {
            return;
        }

        const commandName = departmentId == null ? 'printRecSubtotalAdjustment' : 'printRecItemAdjustment';

        return new XMLNode(commandName, {
            description: priceChange.description,
            justification: 2,
            department: departmentId,
            adjustmentType: adjustmentType,
            amount: Math.abs(amount).toString().replace(".", ",")
        });
    }

    public applyPriceChanges(priceChanges: SalesPriceChanges[] | SalesItemsPriceChanges[], documentXml: XMLNode, departmentId?: number, options?: { xml7Mode?: boolean }) {
        for (const priceChange of priceChanges) {
            const pcAmount = priceChange.amount;

            if (pcAmount) {
                const pcCommand = this.getPriceChangeCommand(priceChange, pcAmount, departmentId, options);

                if (pcCommand) {
                    documentXml.appendChild(pcCommand);
                }
            }
        }
    }

    public applyFiscalBody(documentXml: XMLNode, sale: Sales, options: { xml7Mode?: boolean, printDescription?: boolean, printNotes?: boolean, printerColumns?: number, itemsMap?: Record<number, Items> }) {
        const appendItems = (items: SalesItems[]) => {
            for (const si of items) {
                const originalItem = si.item_id ? options?.itemsMap?.[si.item_id] || undefined : undefined;

                this.saleItemToCommands(documentXml, si, {
                    xml7Mode: options?.xml7Mode,
                    description: options?.printDescription ? originalItem?.description : undefined,
                    printNotes: options?.printNotes,
                    printerColumns: options?.printerColumns
                });

                if(si.price_changes?.length) {
                    this.applyPriceChanges(si.price_changes, documentXml, si.department_id, options);
                }
            }
        };
        
        const appendPriceChanges = (priceChanges: SalesPriceChanges[] | SalesItemsPriceChanges[]) => {
            this.applyPriceChanges(priceChanges, documentXml, undefined, options);
        }

        const appendSubtotal = () => {
            documentXml.appendChild(new XMLNode("printRecSubtotal", { option: 1 }));
        }

        this.fiscalUtils.applyFiscalBody(sale, appendItems, appendPriceChanges, appendSubtotal);
    }

    public saleItemToCommands(documentXml: XMLNode, saleItem: SalesItems, options?: { xml7Mode?: boolean, description?: string, printNotes?: boolean, printerColumns?: number }) {
        let nodeType;

        let nodeContent = {
            description: saleItem.name || saleItem.department_name,
            department: saleItem.department_id
        };

        switch (saleItem.type) {
            case 'sale':
                nodeType = "printRecItem";
                break;
            case 'refund':
                nodeType = "printRecRefund";
                break;
            case 'deposit_cancellation':
            case 'coupon':
                nodeType = options?.xml7Mode ? 'printRecItemAdjustment' : "printRecRefund";
                break;
        }

        switch (nodeType) {
            case 'printRecItem':
            case 'printRecRefund':
                Object.assign(nodeContent, {
                    quantity: Math.abs(saleItem.quantity),
                    unitPrice: saleItem.price.toString().replace(".", ","),
                });
                break;
            case 'printRecItemAdjustment':
                Object.assign(nodeContent, {
                    amount: saleItem.price.toString().replace(".", ","),
                    adjustmentType: saleItem.type === 'deposit_cancellation' ? 10 : 12
                });
                break;
        }

        documentXml.appendChild(new XMLNode(nodeType, nodeContent));

        if (options?.printNotes) {
            // Item barcode (if print_notes)
            if (saleItem.barcode) {
                const barcode = saleItem.barcode.toLowerCase();

                if (!barcode.includes('p') && !barcode.includes('q')) {
                    documentXml.appendChild(new XMLNode("printRecMessage", {
                        messageType: 4,
                        message: saleItem.barcode
                    }));
                }
            }

            // Notes
            if (saleItem.notes) {
                const notesLines = stringToLines(saleItem.notes, options?.printerColumns);

                for (let noteLine of notesLines) {
                    const nl = noteLine.trim();

                    if (nl) {
                        documentXml.appendChild(new XMLNode("printRecMessage", {
                            messageType: 4,
                            message: nl
                        }));
                    }
                }
            }
        }

        //Description
        if (options?.description) {
            const descriptionLines = stringToLines(options.description, 25);

            for (let descriptionLine of descriptionLines) {
                const dl = descriptionLine.trim();

                if (dl) {
                    documentXml.appendChild(new XMLNode("printRecMessage", {
                        messageType: 4,
                        message: dl
                    }));
                }
            }
        }
    }

    public extractPayments(sale: Sales, resources?: { departments: Departments[] }, options?: any) {
        const xml7Mode = !!(options?.xml7Mode);
        const payments: (FiscalPayment & { index?: number })[] = this.fiscalUtils.extractPayments(sale);

        let saleType = 0; //Default to mixed

        if (xml7Mode && Array.isArray(resources?.departments)) {
            const departmentsById = keyBy(resources!.departments, (department) => department.id);
            const itemsBySalesType = groupBy(sale.sale_items || [], (saleItem) => departmentsById[saleItem.department_id]?.sales_type);

            if (itemsBySalesType.goods && itemsBySalesType.services) {
                saleType = 0; //Mixed sales type case
            } else if (itemsBySalesType.goods) {
                saleType = 1;
            } else if (itemsBySalesType.services) {
                saleType = 2;
            }
        }

        for (let payment of payments) {
            //Assign payment type
            switch (payment.method_type_id) {
                case 1: case 19: case 21: case 32: case 38: case 39: case 40:
                    payment.payment_type = 0; //Cash
                    break;
                case 3:
                    payment.payment_type = 1; //Cheque
                    break;
                case 4: case 5: case 8: case 11: case 13: case 14: case 15: case 17: case 18: case 27: case 30: case 31: case 35: case 37: case 44: case 45:
                    payment.payment_type = 2; //Credit / Credit card
                    break;
                case 20:
                    payment.payment_type = 3; //Ticket
                    break;
                case 6: case 34:
                    payment.payment_type = xml7Mode ? 4 : 3; //Ticket with number (xml7) - Ticket (legacy)
                    break;
                case 2: case 22: case 23: case 24: case 25: case 28: case 29: case 36: case 41: case 43:
                    payment.payment_type = xml7Mode ? 5 : 3; //Unclaimed (xml7) - Ticket (legacy)
                    break;
                case 10: case 26: case 33:
                    payment.payment_type = xml7Mode ? 6 : 3; //Discount on payment (xml7) - Ticket (legacy)
                    break;
                case 16: case 42: case 46:
                    payment.payment_type = payment.unclaimed ? 3 : 2; //(Ticket if unclaimed, digital if otherwise)
                    break;
                default:
                    payment.payment_type = 0;
                    break;
            }

            //Assign index
            if (xml7Mode) {
                switch (payment.method_type_id) {
                    case 1: case 19: case 21: case 32: case 38: case 39: case 40:
                        //TODO: in xml7 index controls the rounding algorithm
                        payment.index = 0;
                        break;
                    case 2:
                        payment.index = saleType;
                        break;
                    case 6: case 34:
                        payment.index = 1;
                        break;
                    case 10: case 33:
                        payment.index = 1;
                        break;
                    case 22: case 23: case 24: case 28: case 29: case 36: case 41: case 43:
                        payment.index = 3;
                        break;
                    case 25:
                        payment.index = 5;
                        break;
                    case 26:
                        payment.index = 0;
                        break;
                }
            }
        }

        return payments;
    }

    public documentLayoutToCommands(doc: SimplifiedDocument, printerColumns: number = 46) {
        const root = new XMLNode()
        const nfXml = new XMLNode("printerNonFiscal");

        root.appendChild(nfXml);
        nfXml.appendChild(new XMLNode("beginNonFiscal"));

        for (const line of doc) {
            switch (line.type) {
                case 'text': {
                    let content = line.content || '';
                    let font = 1;
                    const style = line.style || {};

                    // Map double width/height and bold styles to the Epson RT "font" parameter
                    if (style.doubleWidth && style.bold) {
                        font = 4;
                    } else if (style.doubleHeight) {
                        font = 3;
                    } else if (style.doubleWidth || style.bold) {
                        font = 2;
                    }

                    // Map alignments using horizontal padding
                    if (style.align) {
                        const align = style.align;

                        if (align === EpsonAlign.CENTER) {
                            content = padCenter(content, printerColumns, ' ');
                        } else if (align === EpsonAlign.RIGHT) {
                            content = content.padStart(printerColumns, ' ');
                        }
                    }

                    // Sanitize XML reserved characters (e.g. <, >)
                    const sanitizedContent = content
                        .replace(/[<>]/g, ' ')
                        .replace(String.fromCharCode(160), ' ');

                    nfXml.appendChild(new XMLNode("printNormal", {
                        data: sanitizedContent,
                        font: font
                    }));
                    break;
                }

                case 'barcode': {
                    nfXml.appendChild(new XMLNode("printBarCode", {
                        position: 180,
                        width: line.width || 2,
                        height: line.height || 66,
                        hRIPosition: line.hri || "2",
                        hRIFont: line.font || "B",
                        codeType: line.format || "CODE39",
                        code: line.value
                    }));
                    break;
                }

                case 'qrcode': {
                    nfXml.appendChild(new XMLNode("printBarCode", {
                        qRCodeAlignment: 1,
                        qRCodeSize: line.size || 4,
                        codeType: "QRCODE2",
                        code: line.value
                    }));
                    break;
                }

                case 'logo': {
                    break;
                }

                case 'pulse': {
                    nfXml.appendChild(new XMLNode("openDrawer"));
                    break;
                }
            }
        }

        nfXml.appendChild(new XMLNode("endNonFiscal"));

        return root.toString();
    }
};

angular.module('printers').service('epsonUtils', EpsonUtilsService);
